import React, { useState } from 'react';
import { ClaimBanner } from './components/ClaimBanner/ClaimBanner';
import { ClaimState, OpportunityData, ClaimRecordData, ViewerData } from './components/ClaimBanner/types';
import { Settings2, Layout, Smartphone, BookOpen, ChevronDown } from 'lucide-react';
export function App() {
  const [currentState, setCurrentState] = useState<ClaimState>('eligible');
  const [slaHours, setSlaHours] = useState<number>(48);
  // Mock Data
  const mockOpportunity: OpportunityData = {
    id: '006xx000001aB2cAAE',
    name: 'Smith Refinance - 2024',
    stageName: 'App Out',
    amount: 450000,
    closeDate: '2024-12-31',
    claimed__c: ['in-progress-healthy', 'in-progress-nearing-timeout', 'in-progress-taken-over', 'successful', 'permission-limited'].includes(currentState),
    claim_Date__c: '2024-05-18T14:30:00Z',
    app_Out_10_Days__c: true,
    credit_Pulled_10_Days__c: false,
    package_Back_10_Days__c: false,
    withdrawn_10_Days__c: false,
    credit_Alert_List__c: false,
    credit_Retention_List__c: false
  };
  // Calculate a future date for the countdown based on SLA and current state
  const getNextCutoff = () => {
    const now = new Date();
    if (currentState === 'in-progress-nearing-timeout') {
      // 2 hours from now
      return new Date(now.getTime() + 2 * 60 * 60 * 1000).toISOString();
    }
    // SLA hours from now
    return new Date(now.getTime() + slaHours * 60 * 60 * 1000).toISOString();
  };
  const mockClaim: ClaimRecordData = {
    id: 'a0Bxx000001cD4eEAF',
    status__c: 'In Progress',
    claim_Type__c: 'App Out',
    team_Member__c: {
      name: 'Sarah Jenkins'
    },
    claim_Date__c: '2024-05-18T14:30:00Z',
    returned_to_10_List__c: currentState === 'returned',
    nextCutoffDate: getNextCutoff()
  };
  const mockViewer: ViewerData = {
    hasAudienceAccess: currentState !== 'permission-limited',
    app_Out_10_Day_User__c: true,
    credit_Pulled_10_Day_User__c: false,
    package_Back_10_Day_User__c: false,
    withdrawn_10_Day_User__c: false,
    credit_Alert_List_User__c: false,
    credit_Retention_User__c: false
  };
  const states: {
    value: ClaimState;
    label: string;
  }[] = [{
    value: 'eligible',
    label: '1. No active claim (eligible)'
  }, {
    value: 'not-eligible',
    label: '2. No active claim (not eligible)'
  }, {
    value: 'in-progress-healthy',
    label: '3. In Progress - Loan Claimed (healthy)'
  }, {
    value: 'in-progress-nearing-timeout',
    label: '4. In Progress - Loan Claimed (nearing timeout)'
  }, {
    value: 'in-progress-taken-over',
    label: '5. In Progress - Taken Over'
  }, {
    value: 'successful',
    label: '6. Successful Claim'
  }, {
    value: 'returned',
    label: '7. Returned to List'
  }, {
    value: 'retained',
    label: '8. Retained by Owner'
  }, {
    value: 'permission-limited',
    label: '9. Permission-limited viewer'
  }];
  return <div className="min-h-screen bg-slate-100 font-sans text-slate-900 pb-20" data-id="element-0">
      {/* Sticky Control Bar */}
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1">
        <div className="flex items-center gap-2 text-slate-800 font-semibold" data-id="element-2">
          <Settings2 className="w-5 h-5 text-brand-blue" style={{
          color: '#0176D3'
        }} data-id="element-3" />
          <span data-id="element-4">Prototype Controls</span>
        </div>

        <div className="flex items-center gap-4 flex-wrap" data-id="element-5">
          <div className="flex items-center gap-2" data-id="element-6">
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-7">
              State
            </label>
            <div className="relative" data-id="element-8">
              <select value={currentState} onChange={e => setCurrentState(e.target.value as ClaimState)} className="appearance-none bg-slate-50 border border-slate-300 text-slate-700 py-1.5 pl-3 pr-8 rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-9">
                {states.map(s => <option key={s.value} value={s.value} data-id="element-10">
                    {s.label}
                  </option>)}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" data-id="element-11" />
            </div>
          </div>

          <div className="h-6 w-px bg-slate-300 hidden sm:block" data-id="element-12"></div>

          <div className="flex items-center gap-2" data-id="element-13">
            <label className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-14">
              SLA Badge
            </label>
            <div className="flex bg-slate-100 p-0.5 rounded-md border border-slate-200" data-id="element-15">
              <button onClick={() => setSlaHours(24)} className={`px-3 py-1 text-xs font-medium rounded ${slaHours === 24 ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-16">
                24h
              </button>
              <button onClick={() => setSlaHours(48)} className={`px-3 py-1 text-xs font-medium rounded ${slaHours === 48 ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-17">
                48h
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-8 space-y-12" data-id="element-18">
        {/* Banner Showcase */}
        <section className="space-y-4" data-id="element-19">
          <div className="flex items-center gap-2 text-lg font-semibold text-slate-800 border-b border-slate-200 pb-2" data-id="element-20">
            <Layout className="w-5 h-5" data-id="element-21" />
            <h2 data-id="element-22">Claim Banner</h2>
          </div>
          <ClaimBanner state={currentState} opportunity={mockOpportunity} claim={mockClaim} viewer={mockViewer} slaHours={slaHours} data-id="element-23" />
        </section>

        {/* Compact Responsive Showcase */}
        <section className="space-y-4" data-id="element-24">
          <div className="flex items-center gap-2 text-lg font-semibold text-slate-800 border-b border-slate-200 pb-2" data-id="element-25">
            <Smartphone className="w-5 h-5" data-id="element-26" />
            <h2 data-id="element-27">Compact Responsive Variant (~480px)</h2>
          </div>
          <div className="bg-slate-200 p-8 rounded-lg flex justify-center border border-slate-300 border-dashed" data-id="element-28">
            <div className="w-full max-w-[480px]" data-id="element-29">
              <ClaimBanner state={currentState} opportunity={mockOpportunity} claim={mockClaim} viewer={mockViewer} slaHours={slaHours} compact data-id="element-30" />
            </div>
          </div>
        </section>

        {/* Interaction Notes */}
        
      </div>
    </div>;
}